import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  CircularProgress,
  Dialog,
  IconButton,
  AppBar,
  Toolbar,
  Slide,
  Button,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Alert,
  Switch,
  FormControlLabel,
  Fab,
  Paper,
  Stack,
  Chip,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import PlaceIcon from '@mui/icons-material/Place';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import Tooltip from '@mui/material/Tooltip';
import { festivals as festivalsApi, mapApi } from '../services/api';
import { useFestival } from '../context/FestivalContext';
import { useSidebarActions } from './admin/SidebarActionsContext';
import {
  getLandmarkMeta,
  LANDMARK_META,
  KNOWN_LANDMARK_KINDS,
} from './map/landmarkIcons';

const CELL_COLORS = {
  booth: '#1976d2',
  walkway: '#bdbdbd',
  food: '#ffb300',
  empty: '#fff',
  selected: '#4caf50',
  batchSelected: '#81c784',
};

const BASE_CELL_SIZE = 30;

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ManageMap = ({ embedded = false } = {}) => {
  const { setRightPanel } = useSidebarActions();
  const [cells, setCells] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedCell, setSelectedCell] = useState(null);
  const [editDialog, setEditDialog] = useState(false);
  const [editData, setEditData] = useState({
    cell_type: '',
    cell_name: '',
    section: '',
    is_corner: false,
  });
  const [editError, setEditError] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const [batchSelectedCells, setBatchSelectedCells] = useState(new Set());
  const [batchEditDialog, setBatchEditDialog] = useState(false);
  const [batchEditData, setBatchEditData] = useState({
    cell_type: '',
    cell_name: '',
    section: '',
    is_corner: false,
  });
  const [selectionStart, setSelectionStart] = useState(null);
  const [selectionEnd, setSelectionEnd] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [festivalList, setFestivalList] = useState([]);
  const [festivalId, setFestivalId] = useState(null);
  const [activeFestival, setActiveFestival] = useState(null);
  const [setupMessage, setSetupMessage] = useState(null);
  const [expandDialog, setExpandDialog] = useState(null);
  const [expandCount, setExpandCount] = useState(1);
  const [expandMode, setExpandMode] = useState('end'); // 'end' | 'start' | 'at'
  const [expandAt, setExpandAt] = useState(1);
  const [expandLoading, setExpandLoading] = useState(false);
  const [landmarks, setLandmarks] = useState([]);
  const [showLandmarks, setShowLandmarks] = useState(false);
  const [placingLandmark, setPlacingLandmark] = useState(false);
  // landmarkDialog: null | { mode: 'create'|'edit', data: {...} }
  const [landmarkDialog, setLandmarkDialog] = useState(null);
  const [landmarkError, setLandmarkError] = useState('');
  const [landmarkSaving, setLandmarkSaving] = useState(false);
  const navigate = useNavigate();
  const { refresh: refreshFestivalContext } = useFestival();

  const MODAL_BG = '#f5f6fa';

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.2, 2));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.2, 0.5));
  };

  const handleResetZoom = () => {
    setZoom(1);
  };

  const loadFestivals = useCallback(async () => {
    const all = await festivalsApi.list();
    setFestivalList(all);
    const active = all.find(f => f.is_active) || null;
    setActiveFestival(active);
    setFestivalId((prev) => {
      if (prev && all.some(f => f.id === prev)) return prev;
      return active?.id ?? all[0]?.id ?? null;
    });
    return all;
  }, []);

  const fetchMap = useCallback(async (fid) => {
    if (!fid) {
      setCells([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      setError('');
      const data = await mapApi.get(fid);
      setCells(data);
    } catch (err) {
      const message = err.response?.data?.error || err.message || 'Failed to fetch map';
      setError(message);
      setCells([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchLandmarks = useCallback(async (fid) => {
    if (!fid) {
      setLandmarks([]);
      return;
    }
    try {
      const data = await mapApi.landmarks.list(fid);
      setLandmarks(Array.isArray(data) ? data : []);
    } catch (err) {
      // Non-fatal — log so the cell grid still loads even if landmarks API
      // hiccups. The sidebar will just show an empty list.
      console.warn('Failed to load landmarks:', err);
      setLandmarks([]);
    }
  }, []);

  useEffect(() => {
    loadFestivals().catch(() => {
      setError('Failed to load festivals.');
      setLoading(false);
    });
  }, [loadFestivals]);

  useEffect(() => {
    if (festivalId) {
      fetchMap(festivalId);
      fetchLandmarks(festivalId);
    }
  }, [festivalId, fetchMap, fetchLandmarks]);

  const handleActivateFestival = async () => {
    if (!festivalId) return;
    try {
      setSetupMessage(null);
      await festivalsApi.activate(festivalId);
      await loadFestivals();
      await refreshFestivalContext();
      setSetupMessage('Festival is now active. New vendor registrations will use this year.');
    } catch (err) {
      setSetupMessage(err.response?.data?.error || 'Failed to activate festival');
    }
  };

  const handleExpandGrid = async () => {
    if (!festivalId || !expandDialog) return;
    const kind = expandDialog;
    const count = expandCount;
    const axisMax = kind === 'rows' ? height : width;
    let position = null;
    if (expandMode === 'start') position = 1;
    else if (expandMode === 'at') {
      position = Math.min(axisMax + 1, Math.max(1, Number(expandAt) || 1));
    }
    try {
      setExpandLoading(true);
      setSetupMessage(null);
      if (kind === 'rows') {
        await mapApi.expandRows(festivalId, count, position);
      } else {
        await mapApi.expandColumns(festivalId, count, position);
      }
      setExpandDialog(null);
      setExpandCount(1);
      setExpandMode('end');
      setExpandAt(1);
      await fetchMap(festivalId);
      const where =
        position == null
          ? kind === 'rows' ? 'at the bottom' : 'at the right'
          : kind === 'rows'
            ? `starting at row ${position}`
            : `starting at column ${position}`;
      setSetupMessage(
        kind === 'rows'
          ? `Added ${count} row(s) ${where}.`
          : `Added ${count} column(s) ${where}.`
      );
    } catch (err) {
      setSetupMessage(err.response?.data?.error || 'Failed to expand map');
    } finally {
      setExpandLoading(false);
    }
  };

  // Find grid size
  const width = Math.max(...cells.map(c => c.x_coordinate), 0);
  const height = Math.max(...cells.map(c => c.y_coordinate), 0);
  const selectedFestival = festivalList.find(f => f.id === festivalId);
  const selectedFestivalLabel = selectedFestival
    ? `${selectedFestival.name} · ${width}×${height} grid`
    : '';

  // Build grid
  const grid = Array.from({ length: height }, (_, y) =>
    Array.from({ length: width }, (_, x) =>
      cells.find(c => c.x_coordinate === x + 1 && c.y_coordinate === y + 1)
    )
  );

  const handleClose = () => {
    navigate(-1);
  };

  const handleCellClick = (cell, gridX, gridY) => {
    // Placement mode wins: the next grid click drops a pin at that cell's
    // center instead of opening the cell editor. Coordinate convention matches
    // the visitor map: landmark.x = N means the center of cell N.
    if (placingLandmark) {
      const x = gridX || cell?.x_coordinate;
      const y = gridY || cell?.y_coordinate;
      if (!x || !y) return;
      setPlacingLandmark(false);
      setLandmarkError('');
      setLandmarkDialog({
        mode: 'create',
        data: {
          kind: 'restroom',
          label: '',
          notes: '',
          x_coordinate: Number(x),
          y_coordinate: Number(y),
          sort_order: 0,
        },
      });
      return;
    }
    if (!isDragging) {
      setSelectedCell(cell);
      setEditData({
        cell_type: cell?.cell_type || '',
        cell_name: cell?.cell_name || '',
        section: cell?.section || '',
        is_corner: cell?.is_corner || false,
      });
      setEditDialog(true);
    }
  };

  const openLandmarkEdit = (landmark) => {
    setLandmarkError('');
    setLandmarkDialog({
      mode: 'edit',
      data: {
        id: landmark.id,
        kind: landmark.kind || 'other',
        label: landmark.label || '',
        notes: landmark.notes || '',
        x_coordinate: Number(landmark.x_coordinate),
        y_coordinate: Number(landmark.y_coordinate),
        sort_order: Number(landmark.sort_order || 0),
      },
    });
  };

  const handleStartPlacingLandmark = () => {
    setLandmarkError('');
    setPlacingLandmark(true);
    setShowLandmarks(true);
  };

  const handleCancelPlacing = () => setPlacingLandmark(false);

  const handleLandmarkSave = async () => {
    if (!landmarkDialog || !festivalId) return;
    const { mode, data } = landmarkDialog;
    const payload = {
      kind: String(data.kind || '').trim(),
      label: data.label?.trim() || null,
      notes: data.notes?.trim() || null,
      xCoordinate: Number(data.x_coordinate),
      yCoordinate: Number(data.y_coordinate),
      sortOrder: Number(data.sort_order) || 0,
    };
    if (!payload.kind) {
      setLandmarkError('Kind is required.');
      return;
    }
    if (!Number.isFinite(payload.xCoordinate) || !Number.isFinite(payload.yCoordinate)) {
      setLandmarkError('Coordinates must be numbers.');
      return;
    }
    try {
      setLandmarkSaving(true);
      setLandmarkError('');
      if (mode === 'create') {
        await mapApi.landmarks.create(festivalId, payload);
      } else {
        await mapApi.landmarks.update(data.id, payload);
      }
      await fetchLandmarks(festivalId);
      setLandmarkDialog(null);
    } catch (err) {
      setLandmarkError(err.response?.data?.error || err.message || 'Failed to save landmark');
    } finally {
      setLandmarkSaving(false);
    }
  };

  const handleLandmarkDelete = async () => {
    if (!landmarkDialog || landmarkDialog.mode !== 'edit') return;
    const id = landmarkDialog.data?.id;
    if (!id) return;
    if (!window.confirm('Delete this landmark? This cannot be undone.')) return;
    try {
      setLandmarkSaving(true);
      setLandmarkError('');
      await mapApi.landmarks.remove(id);
      await fetchLandmarks(festivalId);
      setLandmarkDialog(null);
    } catch (err) {
      setLandmarkError(err.response?.data?.error || err.message || 'Failed to delete landmark');
    } finally {
      setLandmarkSaving(false);
    }
  };

  const handleMouseDown = (cell) => {
    if (!cell) return;
    setIsDragging(true);
    setSelectionStart({ x: cell.x_coordinate, y: cell.y_coordinate });
    setSelectionEnd({ x: cell.x_coordinate, y: cell.y_coordinate });
    updateSelectedCells(cell.x_coordinate, cell.y_coordinate, cell.x_coordinate, cell.y_coordinate);
  };

  const handleMouseMove = (cell) => {
    if (isDragging && cell && selectionStart) {
      setSelectionEnd({ x: cell.x_coordinate, y: cell.y_coordinate });
      updateSelectedCells(
        selectionStart.x,
        selectionStart.y,
        cell.x_coordinate,
        cell.y_coordinate
      );
    }
  };

  const updateSelectedCells = (startX, startY, endX, endY) => {
    const minX = Math.min(startX, endX);
    const maxX = Math.max(startX, endX);
    const minY = Math.min(startY, endY);
    const maxY = Math.max(startY, endY);

    const selectedIds = new Set();
    cells.forEach(cell => {
      if (cell.x_coordinate >= minX && cell.x_coordinate <= maxX &&
          cell.y_coordinate >= minY && cell.y_coordinate <= maxY) {
        selectedIds.add(cell.id);
      }
    });
    setBatchSelectedCells(selectedIds);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  const handleBatchEditClick = () => {
    if (batchSelectedCells.size > 0) {
      setBatchEditDialog(true);
    }
  };

  const handleEditSubmit = async () => {
    try {
      setEditError('');

      await mapApi.updateCell(selectedCell?.id, editData);
      if (festivalId) await fetchMap(festivalId);
      setEditDialog(false);
    } catch (err) {
      setEditError(err.message);
    }
  };

  const handleBatchEditSubmit = async () => {
    try {
      setEditError('');
      await mapApi.batchUpdate(Array.from(batchSelectedCells), batchEditData);
      if (festivalId) await fetchMap(festivalId);
      setBatchEditDialog(false);
      setBatchSelectedCells(new Set());
    } catch (err) {
      setEditError(err.message);
    }
  };

  const getCellColor = (cell) => {
    if (!cell) return CELL_COLORS.empty;
    if (batchSelectedCells.has(cell.id)) return CELL_COLORS.batchSelected;
    if (cell === selectedCell) return CELL_COLORS.selected;
    return CELL_COLORS[cell.cell_type] || CELL_COLORS.empty;
  };

  // Tools panel — shared between fullscreen AppBar and the embedded right
  // sidebar. Uses `direction` to switch between row layout (top toolbar) and
  // column layout (sidebar).
  const renderTools = (direction = 'row') => {
    const isRow = direction === 'row';
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: direction,
          gap: isRow ? 1 : 1.5,
          alignItems: isRow ? 'center' : 'stretch',
          width: isRow ? 'auto' : '100%',
        }}
      >
        {festivalList.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 200 }} fullWidth={!isRow}>
            <InputLabel>Festival</InputLabel>
            <Select
              label="Festival"
              value={festivalId ?? ''}
              onChange={(e) => setFestivalId(Number(e.target.value))}
            >
              {festivalList.map(f => (
                <MenuItem key={f.id} value={f.id}>
                  {f.name}{f.is_active ? ' (active)' : ''}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <Button
          size="small"
          variant={isRow ? 'text' : 'outlined'}
          startIcon={<AddIcon />}
          onClick={() => setExpandDialog('rows')}
          disabled={!festivalId || cells.length === 0}
          fullWidth={!isRow}
          sx={isRow ? { mr: 1 } : { justifyContent: 'flex-start' }}
        >
          Add rows
        </Button>
        <Button
          size="small"
          variant={isRow ? 'text' : 'outlined'}
          startIcon={<AddIcon />}
          onClick={() => setExpandDialog('columns')}
          disabled={!festivalId || cells.length === 0}
          fullWidth={!isRow}
          sx={isRow ? { mr: 1 } : { justifyContent: 'flex-start' }}
        >
          Add columns
        </Button>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            background: '#fff',
            borderRadius: 1,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            p: 0.5,
            justifyContent: isRow ? 'flex-start' : 'center',
            alignSelf: isRow ? 'auto' : 'center',
          }}
        >
          <Button
            variant="text"
            size="small"
            onClick={handleZoomOut}
            disabled={zoom <= 0.5}
            sx={{ minWidth: 32, height: 32, borderRadius: 1, color: '#666' }}
          >
            −
          </Button>
          <Button
            variant="text"
            size="small"
            onClick={handleResetZoom}
            sx={{ minWidth: 64, height: 32, borderRadius: 1, color: '#666', fontSize: '0.875rem' }}
          >
            {Math.round(zoom * 100)}%
          </Button>
          <Button
            variant="text"
            size="small"
            onClick={handleZoomIn}
            disabled={zoom >= 2}
            sx={{ minWidth: 32, height: 32, borderRadius: 1, color: '#666' }}
          >
            +
          </Button>
        </Box>
        {batchSelectedCells.size > 0 && (
          <Button
            size="small"
            variant="contained"
            startIcon={<EditIcon />}
            onClick={handleBatchEditClick}
            fullWidth={!isRow}
          >
            Edit {batchSelectedCells.size} cell{batchSelectedCells.size === 1 ? '' : 's'}
          </Button>
        )}
      </Box>
    );
  };

  // Sidebar landmarks panel — only useful when laid out as a column. Lists
  // existing pins, lets the admin toggle their visibility on the editor, and
  // start placing a new one.
  const renderLandmarksPanel = () => (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700, flex: 1 }}>
          Landmarks
        </Typography>
        <Chip
          label={landmarks.length}
          size="small"
          sx={{ height: 20, fontSize: 11 }}
        />
      </Stack>

      <Stack spacing={1}>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={showLandmarks}
              onChange={(e) => setShowLandmarks(e.target.checked)}
            />
          }
          label={
            <Stack direction="row" alignItems="center" spacing={0.5}>
              {showLandmarks ? (
                <VisibilityIcon fontSize="small" color="action" />
              ) : (
                <VisibilityOffIcon fontSize="small" color="action" />
              )}
              <Typography variant="body2">Show on map</Typography>
            </Stack>
          }
        />

        {placingLandmark ? (
          <Alert
            severity="info"
            action={
              <Button color="inherit" size="small" onClick={handleCancelPlacing}>
                Cancel
              </Button>
            }
            sx={{ py: 0.5 }}
          >
            Click a cell to drop the pin.
          </Alert>
        ) : (
          <Button
            size="small"
            variant="outlined"
            startIcon={<PlaceIcon />}
            onClick={handleStartPlacingLandmark}
            disabled={!festivalId || cells.length === 0}
            fullWidth
            sx={{ justifyContent: 'flex-start' }}
          >
            Add landmark
          </Button>
        )}

        {landmarks.length === 0 ? (
          <Typography variant="caption" color="text.secondary">
            No landmarks yet. Add restrooms, food, stages, info booths, etc. to
            help visitors find their way on the public map.
          </Typography>
        ) : (
          <Stack
            spacing={0.5}
            sx={{
              maxHeight: 260,
              overflowY: 'auto',
              pr: 0.5,
              mt: 0.5,
            }}
          >
            {landmarks.map((lm) => {
              const meta = getLandmarkMeta(lm.kind);
              const Icon = meta.Icon;
              return (
                <Box
                  key={lm.id}
                  onClick={() => openLandmarkEdit(lm)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 0.75,
                    borderRadius: 1,
                    cursor: 'pointer',
                    '&:hover': { background: '#f1f3f6' },
                  }}
                >
                  <Box
                    sx={{
                      width: 22,
                      height: 22,
                      borderRadius: '50%',
                      background: '#fff',
                      border: `2px solid ${meta.color}`,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Icon sx={{ color: meta.color, fontSize: 14 }} />
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {lm.label || meta.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {meta.label} · ({Number(lm.x_coordinate).toFixed(1)},{' '}
                      {Number(lm.y_coordinate).toFixed(1)})
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Stack>
        )}
      </Stack>
    </Box>
  );

  // When embedded inside the admin shell, push the tools panel into the
  // right sidebar via SidebarActionsContext. Fall back to the default panels
  // when the component unmounts.
  useEffect(() => {
    if (!embedded) return undefined;
    setRightPanel(
      <Box sx={{ p: 2 }}>
        <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 700 }}>
          Map Tools
        </Typography>
        <Box sx={{ mt: 1 }}>
          {renderTools('column')}
        </Box>
        <Divider sx={{ my: 2 }} />
        <Typography variant="caption" color="text.secondary">
          Click a cell to edit it. Drag across cells to batch edit.
        </Typography>
        <Divider sx={{ my: 2 }} />
        {renderLandmarksPanel()}
      </Box>
    );
    return () => setRightPanel(null);
    // We deliberately re-run whenever the data backing the tools panel
    // changes so the sidebar always renders the current state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    embedded,
    festivalList,
    festivalId,
    cells.length,
    zoom,
    batchSelectedCells.size,
    setRightPanel,
    landmarks,
    showLandmarks,
    placingLandmark,
  ]);

  const mapContent = (
    <>
    <Box sx={{
      p: 2,
      flex: 1,
      overflow: 'auto',
      background: MODAL_BG,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'flex-start',
      minHeight: embedded ? 'calc(100vh - 64px - 32px)' : undefined,
    }}>
        {setupMessage && (
          <Alert
            severity="info"
            onClose={() => setSetupMessage(null)}
            sx={{ mb: 2, maxWidth: 720, width: '100%' }}
          >
            {setupMessage}
          </Alert>
        )}

        {festivalList.length === 0 && !loading && (
          <Paper sx={{ p: 4, maxWidth: 520, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              No festivals yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Create a festival for the new year, then clone last year&apos;s map and adjust the layout here.
            </Typography>
            <Button component={Link} to="/admin/festivals" variant="contained">
              Create a festival
            </Button>
          </Paper>
        )}

        {festivalList.length > 0 && !activeFestival && (
          <Paper sx={{ p: 3, mb: 3, maxWidth: 640, width: '100%' }}>
            <Stack spacing={2}>
              <Typography variant="h6">No active festival</Typography>
              <Typography variant="body2" color="text.secondary">
                Vendor registration and the public map use whichever festival is marked active.
                You can still edit any year&apos;s map below, then make that festival active when you are ready.
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                <FormControl size="small" sx={{ minWidth: 220, flex: 1 }}>
                  <InputLabel>Festival to activate</InputLabel>
                  <Select
                    label="Festival to activate"
                    value={festivalId ?? ''}
                    onChange={(e) => setFestivalId(Number(e.target.value))}
                  >
                    {festivalList.map(f => (
                      <MenuItem key={f.id} value={f.id}>{f.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button variant="contained" onClick={handleActivateFestival} disabled={!festivalId}>
                  Make active
                </Button>
                <Button component={Link} to="/admin/festivals" variant="outlined">
                  Create / manage festivals
                </Button>
              </Stack>
            </Stack>
          </Paper>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2, maxWidth: 720, width: '100%' }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
            <CircularProgress />
          </Box>
        ) : festivalId && cells.length === 0 && !error ? (
          <Paper sx={{ p: 4, maxWidth: 560, textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              No map for this festival yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Clone a map from a previous year on the Festivals page, then return here to add rows or columns if the fairgrounds layout changed.
            </Typography>
            <Button component={Link} to="/admin/festivals" variant="contained">
              Go to Festivals — clone map
            </Button>
          </Paper>
        ) : cells.length > 0 ? (
          <Box sx={{ position: 'relative' }}>
            {selectedFestivalLabel && (
              <Chip
                label={selectedFestivalLabel}
                size="small"
                sx={{ mb: 1 }}
                color={activeFestival?.id === festivalId ? 'primary' : 'default'}
              />
            )}
            <Box 
              sx={{ 
                background: MODAL_BG, 
                mx: 'auto', 
                p: 2,
                transform: `scale(${zoom})`,
                transformOrigin: 'top center',
                transition: 'transform 0.2s ease-in-out',
              }}
              onMouseLeave={handleMouseLeave}
            >
              <Box sx={{ position: 'relative' }}>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: `repeat(${width}, ${BASE_CELL_SIZE}px)`,
                    gridTemplateRows: `repeat(${height}, ${BASE_CELL_SIZE}px)`,
                    gap: '1px',
                    background: '#888',
                    justifyContent: 'center',
                    userSelect: 'none',
                  }}
                >
                {grid.flat().map((cell, i) => {
                  const gridX = (i % width) + 1;
                  const gridY = Math.floor(i / width) + 1;
                  return (
                  <Box
                    key={i}
                    sx={{
                      width: BASE_CELL_SIZE,
                      height: BASE_CELL_SIZE,
                      background: getCellColor(cell),
                      boxSizing: 'border-box',
                      cursor: placingLandmark
                        ? 'crosshair'
                        : isDragging
                          ? 'crosshair'
                          : 'pointer',
                      transition: 'box-shadow 0.2s, border 0.2s',
                      border: 'none',
                      position: 'relative',
                      '&:hover': {
                        boxShadow: placingLandmark
                          ? '0 0 0 2px #1976d2'
                          : '0 0 0 1px #bbb',
                        zIndex: 1,
                      },
                    }}
                    onClick={() => handleCellClick(cell, gridX, gridY)}
                    onMouseDown={() => !placingLandmark && handleMouseDown(cell)}
                    onMouseMove={() => !placingLandmark && handleMouseMove(cell)}
                    onMouseUp={handleMouseUp}
                    title={
                      placingLandmark
                        ? `Place landmark at (${gridX}, ${gridY})`
                        : cell?.cell_name || cell?.cell_type || 'empty'
                    }
                  >
                    {cell?.cell_name && cell?.cell_type !== 'walkway' && (
                      <Typography
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          fontSize: '12px',
                          lineHeight: 1,
                          color: '#fff',
                          textAlign: 'center',
                          width: '100%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          pointerEvents: 'none',
                        }}
                      >
                        {cell.cell_name}
                      </Typography>
                    )}
                  </Box>
                  );
                })}
                </Box>

                {/* Landmark pin overlay. Sits on top of the grid using the same
                    coordinate convention as the visitor map: landmark.x = N is
                    the center of cell N. We add (x - 1) gap pixels so pins line
                    up with the grid's 1px gutters. */}
                {showLandmarks && landmarks.length > 0 && (
                  <Box
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      pointerEvents: 'none',
                    }}
                  >
                    {landmarks.map((lm) => {
                      const meta = getLandmarkMeta(lm.kind);
                      const Icon = meta.Icon;
                      const lx = Number(lm.x_coordinate);
                      const ly = Number(lm.y_coordinate);
                      if (!Number.isFinite(lx) || !Number.isFinite(ly)) return null;
                      // Pixel position inside the grid. CSS grid has a 1px gap,
                      // so each cell occupies (BASE_CELL_SIZE + 1) px in the
                      // grid's coordinate space; the center of cell N sits at
                      // (N - 0.5) * (BASE_CELL_SIZE + 1) - 0.5.
                      const STEP = BASE_CELL_SIZE + 1;
                      const px = (lx - 0.5) * STEP - 0.5;
                      const py = (ly - 0.5) * STEP - 0.5;
                      return (
                        <Tooltip
                          key={lm.id}
                          title={`${meta.label}${lm.label ? `: ${lm.label}` : ''}`}
                          placement="top"
                          arrow
                        >
                          <Box
                            onClick={(e) => {
                              e.stopPropagation();
                              openLandmarkEdit(lm);
                            }}
                            onMouseDown={(e) => e.stopPropagation()}
                            sx={{
                              position: 'absolute',
                              left: px,
                              top: py,
                              width: 26,
                              height: 26,
                              ml: '-13px',
                              mt: '-13px',
                              borderRadius: '50%',
                              background: '#fff',
                              border: `2px solid ${meta.color}`,
                              boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              cursor: 'pointer',
                              pointerEvents: 'auto',
                              transition: 'transform 0.15s ease',
                              '&:hover': { transform: 'scale(1.1)' },
                            }}
                          >
                            <Icon sx={{ color: meta.color, fontSize: 16 }} />
                          </Box>
                        </Tooltip>
                      );
                    })}
                  </Box>
                )}
              </Box>
            </Box>
            {batchSelectedCells.size > 0 && !embedded && (
              <Fab
                color="primary"
                sx={{
                  position: 'fixed',
                  bottom: 24,
                  right: 24,
                }}
                onClick={handleBatchEditClick}
              >
                <EditIcon />
              </Fab>
            )}
          </Box>
        ) : null}
      </Box>

      <Dialog
        open={Boolean(expandDialog)}
        onClose={() => !expandLoading && setExpandDialog(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {expandDialog === 'rows' ? 'Add rows to map' : 'Add columns to map'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {expandDialog === 'rows'
              ? `New empty cells are inserted as full rows, matching the current map width of ${width}. Existing rows at or below the insertion point shift down. Vendor booth registrations stay attached to their original cells.`
              : `New empty cells are inserted as full columns, matching the current map height of ${height}. Existing columns at or to the right of the insertion point shift right. Vendor booth registrations stay attached to their original cells.`}
          </Typography>
          <Stack spacing={2}>
            <TextField
              fullWidth
              type="number"
              label="How many?"
              inputProps={{ min: 1, max: 50 }}
              value={expandCount}
              onChange={(e) => setExpandCount(Math.max(1, Number(e.target.value) || 1))}
            />
            <FormControl fullWidth>
              <InputLabel>Where</InputLabel>
              <Select
                label="Where"
                value={expandMode}
                onChange={(e) => setExpandMode(e.target.value)}
              >
                <MenuItem value="end">
                  {expandDialog === 'rows' ? 'Append to bottom' : 'Append to right'}
                </MenuItem>
                <MenuItem value="start">
                  {expandDialog === 'rows' ? 'Insert at top (row 1)' : 'Insert at left (column 1)'}
                </MenuItem>
                <MenuItem value="at">
                  {expandDialog === 'rows' ? 'Insert before a specific row' : 'Insert before a specific column'}
                </MenuItem>
              </Select>
            </FormControl>
            {expandMode === 'at' && (
              <TextField
                fullWidth
                type="number"
                label={
                  expandDialog === 'rows'
                    ? `Insert before row (1 – ${Math.max(1, height + 1)})`
                    : `Insert before column (1 – ${Math.max(1, width + 1)})`
                }
                helperText={
                  expandDialog === 'rows'
                    ? `Row ${expandAt} and everything below it will shift down by ${expandCount}.`
                    : `Column ${expandAt} and everything to its right will shift right by ${expandCount}.`
                }
                inputProps={{
                  min: 1,
                  max: (expandDialog === 'rows' ? height : width) + 1,
                }}
                value={expandAt}
                onChange={(e) => {
                  const max = (expandDialog === 'rows' ? height : width) + 1;
                  const v = Math.max(1, Math.min(max, Number(e.target.value) || 1));
                  setExpandAt(v);
                }}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExpandDialog(null)} disabled={expandLoading}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleExpandGrid} disabled={expandLoading}>
            {expandLoading ? 'Adding…' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={editDialog}
        onClose={() => setEditDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Edit Cell {selectedCell?.x_coordinate}, {selectedCell?.y_coordinate}
        </DialogTitle>
        <DialogContent>
          {editError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {editError}
            </Alert>
          )}
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Cell Type</InputLabel>
              <Select
                value={editData.cell_type}
                label="Cell Type"
                onChange={(e) => setEditData(prev => ({
                  ...prev,
                  cell_type: e.target.value
                }))}
              >
                <MenuItem value="booth">Booth</MenuItem>
                <MenuItem value="walkway">Walkway</MenuItem>
                <MenuItem value="food">Food</MenuItem>
                <MenuItem value="empty">Empty</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Cell Name"
              value={editData.cell_name}
              onChange={(e) => setEditData(prev => ({
                ...prev,
                cell_name: e.target.value
              }))}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Section"
              value={editData.section || ''}
              onChange={(e) => setEditData(prev => ({
                ...prev,
                section: e.target.value
              }))}
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={editData.is_corner}
                  onChange={(e) => setEditData(prev => ({
                    ...prev,
                    is_corner: e.target.checked
                  }))}
                />
              }
              label="Is Corner"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>Cancel</Button>
          <Button onClick={handleEditSubmit} variant="contained">
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(landmarkDialog)}
        onClose={() => !landmarkSaving && setLandmarkDialog(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {landmarkDialog?.mode === 'edit' ? 'Edit landmark' : 'Add landmark'}
        </DialogTitle>
        <DialogContent>
          {landmarkError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {landmarkError}
            </Alert>
          )}
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Kind</InputLabel>
              <Select
                label="Kind"
                value={
                  KNOWN_LANDMARK_KINDS.includes(landmarkDialog?.data?.kind)
                    ? landmarkDialog.data.kind
                    : '__custom__'
                }
                onChange={(e) => {
                  const val = e.target.value;
                  setLandmarkDialog((prev) => prev && {
                    ...prev,
                    data: {
                      ...prev.data,
                      kind: val === '__custom__' ? '' : val,
                    },
                  });
                }}
              >
                {KNOWN_LANDMARK_KINDS.map((k) => (
                  <MenuItem key={k} value={k}>
                    {LANDMARK_META[k]?.label || k}
                  </MenuItem>
                ))}
                <MenuItem value="__custom__">Custom…</MenuItem>
              </Select>
            </FormControl>

            {!KNOWN_LANDMARK_KINDS.includes(landmarkDialog?.data?.kind) && (
              <TextField
                fullWidth
                label="Custom kind"
                helperText="Unknown kinds render with the generic pin icon."
                value={landmarkDialog?.data?.kind || ''}
                onChange={(e) =>
                  setLandmarkDialog((prev) => prev && {
                    ...prev,
                    data: { ...prev.data, kind: e.target.value },
                  })
                }
              />
            )}

            <TextField
              fullWidth
              label="Label (optional)"
              placeholder="e.g. Restrooms (Outside Jaycees Building)"
              value={landmarkDialog?.data?.label || ''}
              onChange={(e) =>
                setLandmarkDialog((prev) => prev && {
                  ...prev,
                  data: { ...prev.data, label: e.target.value },
                })
              }
            />

            <Stack direction="row" spacing={1}>
              <TextField
                fullWidth
                type="number"
                label="X (col)"
                inputProps={{ step: 0.1, min: 0.5 }}
                value={landmarkDialog?.data?.x_coordinate ?? ''}
                onChange={(e) =>
                  setLandmarkDialog((prev) => prev && {
                    ...prev,
                    data: { ...prev.data, x_coordinate: Number(e.target.value) },
                  })
                }
              />
              <TextField
                fullWidth
                type="number"
                label="Y (row)"
                inputProps={{ step: 0.1, min: 0.5 }}
                value={landmarkDialog?.data?.y_coordinate ?? ''}
                onChange={(e) =>
                  setLandmarkDialog((prev) => prev && {
                    ...prev,
                    data: { ...prev.data, y_coordinate: Number(e.target.value) },
                  })
                }
              />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Use whole numbers to center on a cell, or add 0.5 to sit between
              cells. The current map is {width} × {height}.
            </Typography>

            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Notes (optional, admin only)"
              value={landmarkDialog?.data?.notes || ''}
              onChange={(e) =>
                setLandmarkDialog((prev) => prev && {
                  ...prev,
                  data: { ...prev.data, notes: e.target.value },
                })
              }
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Box>
            {landmarkDialog?.mode === 'edit' && (
              <Button
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleLandmarkDelete}
                disabled={landmarkSaving}
              >
                Delete
              </Button>
            )}
          </Box>
          <Box>
            <Button onClick={() => setLandmarkDialog(null)} disabled={landmarkSaving}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleLandmarkSave}
              disabled={landmarkSaving}
              sx={{ ml: 1 }}
            >
              {landmarkSaving ? 'Saving…' : 'Save'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Dialog
        open={batchEditDialog}
        onClose={() => setBatchEditDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Batch Edit {batchSelectedCells.size} Cells
        </DialogTitle>
        <DialogContent>
          {editError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {editError}
            </Alert>
          )}
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>Cell Type</InputLabel>
              <Select
                value={batchEditData.cell_type}
                label="Cell Type"
                onChange={(e) => setBatchEditData(prev => ({
                  ...prev,
                  cell_type: e.target.value
                }))}
              >
                <MenuItem value="booth">Booth</MenuItem>
                <MenuItem value="walkway">Walkway</MenuItem>
                <MenuItem value="food">Food</MenuItem>
                <MenuItem value="empty">Empty</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Cell Name"
              value={batchEditData.cell_name}
              onChange={(e) => setBatchEditData(prev => ({
                ...prev,
                cell_name: e.target.value
              }))}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Section"
              value={batchEditData.section || ''}
              onChange={(e) => setBatchEditData(prev => ({
                ...prev,
                section: e.target.value
              }))}
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={batchEditData.is_corner}
                  onChange={(e) => setBatchEditData(prev => ({
                    ...prev,
                    is_corner: e.target.checked
                  }))}
                />
              }
              label="Is Corner"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBatchEditDialog(false)}>Cancel</Button>
          <Button onClick={handleBatchEditSubmit} variant="contained">
            Save All
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );

  if (embedded) {
    return mapContent;
  }

  return (
    <Dialog
      fullScreen
      open
      onClose={handleClose}
      TransitionComponent={Transition}
    >
      <AppBar sx={{ position: 'relative', background: '#fafbfc', color: '#222', boxShadow: 0 }}>
        <Toolbar>
          <Typography sx={{ flex: 1 }} variant="h6" component="div">
            Manage Map
          </Typography>
          {renderTools('row')}
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleClose}
            aria-label="close"
            sx={{ ml: 1 }}
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>
      {mapContent}
    </Dialog>
  );
};

export default ManageMap; 